/**
 * Card templates — DESIGN.md §6.15 (R33).
 *
 * "A template captures title, description, priority, tags, custom-field
 * values, estimate, and a subtask checklist. Global or project-scoped; any
 * member creates; owner/admins edit; archivable."
 *
 * §6.15 also settles what a template is *for*: "External/anonymous form intake
 * stays roadmap (§22) — internally, templates are the form." So the content
 * shape is deliberately the same fields the create-card flow accepts, not a
 * second modelling of a card.
 */
import { z } from 'zod';
import { SCOPES } from '../enums.js';
import { richTextSchema } from '../richtext/schema.js';
import { prioritySchema } from './view-config.js';
import { name, optionalText, uuidSchema } from './common.js';
import { customFieldValuesSchema } from './tasks.js';
import { userRefSchema } from './users.js';
export const templateContentSchema = z
    .object({
    title: z.string().max(500).default(''),
    description: richTextSchema.nullish(),
    priority: prioritySchema.nullish(),
    estimateMinutes: z.number().int().min(0).nullish(),
    tagIds: z.array(uuidSchema).max(50).default([]),
    fieldValues: customFieldValuesSchema.default({}),
    /** §6.15 — "a subtask checklist", pre-filled on every new card. */
    subtasks: z
        .array(z.object({ title: name(500) }))
        .max(50)
        .default([]),
})
    .strict();
export const templateSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    scope: z.enum(SCOPES),
    projectId: uuidSchema.nullable(),
    content: templateContentSchema,
    createdBy: userRefSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
    archivedAt: z.string().nullable(),
    canEdit: z.boolean(),
});
export const createTemplateRequestSchema = z
    .object({
    name: name(120),
    scope: z.enum(SCOPES).default('global'),
    projectId: uuidSchema.nullish(),
    content: templateContentSchema,
})
    .strict();
export const updateTemplateRequestSchema = z
    .object({
    name: name(120).optional(),
    content: templateContentSchema.optional(),
})
    .strict();
export const listTemplatesQuerySchema = z.object({
    projectId: uuidSchema.optional(),
    includeArchived: z
        .enum(['true', 'false'])
        .default('false')
        .transform((value) => value === 'true'),
});
/** Made from an existing card — "save this as a template" (§6.15). */
export const templateFromTaskRequestSchema = z
    .object({
    taskId: uuidSchema,
    name: name(120),
    scope: z.enum(SCOPES).default('global'),
    projectId: uuidSchema.nullish(),
    note: optionalText(500),
})
    .strict();
//# sourceMappingURL=templates.js.map